import type {
  AdminSession,
  BadmintonMatch,
  BadmintonMatchDraft,
  BadmintonTeam,
  BadmintonTeamDraft,
  BadmintonTeamsResponse,
  BuSummary,
  EventSettings,
  FootballTeam,
  Participant,
  ParticipantDraft,
  Sport,
  Stats
} from "./types";

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {})
    }
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export const getEvent = () => api<EventSettings>("/api/event");
export const updateEvent = (event: EventSettings) =>
  api<EventSettings>("/api/event", { method: "PUT", body: JSON.stringify(event) });
export const getSports = () => api<Sport[]>("/api/sports");
export const getStats = () => api<Stats>("/api/stats");
export const getBuSummary = () => api<BuSummary[]>("/api/bu-summary");
export const getParticipants = () => api<Participant[]>("/api/participants");
export const getFootballRoster = () => api<FootballTeam[]>("/api/football-roster");
export const getAdminSession = () => api<AdminSession>("/api/admin-session");
export const adminLogin = (username: string, password: string) =>
  api<AdminSession>("/api/admin-login", {
    method: "POST",
    body: JSON.stringify({ username, password })
  });
export const adminLogout = () =>
  api<AdminSession>("/api/admin-logout", {
    method: "POST"
  });
export const importSheet = () =>
  api<{ imported_participants: number; imported_registrations: number }>("/api/import-sheet", {
    method: "POST"
  });

export const createParticipant = (participant: ParticipantDraft) =>
  api<{ id: number }>("/api/participants", {
    method: "POST",
    body: JSON.stringify(participant)
  });

export const updateParticipant = (id: number, participant: ParticipantDraft) =>
  api<{ id: number }>(`/api/participants/${id}`, {
    method: "PUT",
    body: JSON.stringify(participant)
  });

export const deleteParticipant = (id: number) =>
  api<{ ok: true }>(`/api/participants/${id}`, { method: "DELETE" });

export const upsertRegistration = (participantId: number, sportId: number, teammateName: string) =>
  api<{ id: number }>("/api/registrations", {
    method: "POST",
    body: JSON.stringify({
      participant_id: participantId,
      sport_id: sportId,
      teammate_name: teammateName
    })
  });

export const updateRegistration = (id: number, teammateName: string) =>
  api<{ id: number }>(`/api/registrations/${id}`, {
    method: "PUT",
    body: JSON.stringify({ teammate_name: teammateName })
  });

export const deleteRegistration = (id: number) =>
  api<{ ok: true }>(`/api/registrations/${id}`, { method: "DELETE" });

export const getBadmintonTeams = (sportSlug: string) =>
  api<BadmintonTeamsResponse>(`/api/badminton/${sportSlug}/teams`);

export const importBadmintonSheet = (sportSlug: string) =>
  api<{ imported_teams: number; imported_groups: number }>(`/api/badminton/${sportSlug}/import-sheet`, {
    method: "POST"
  });

export const createBadmintonTeam = (sportSlug: string, team: BadmintonTeamDraft) =>
  api<BadmintonTeam>(`/api/badminton/${sportSlug}/teams`, {
    method: "POST",
    body: JSON.stringify(team)
  });

export const updateBadmintonTeam = (id: number, team: BadmintonTeamDraft) =>
  api<BadmintonTeam>(`/api/badminton/teams/${id}`, {
    method: "PUT",
    body: JSON.stringify(team)
  });

export const deleteBadmintonTeam = (id: number) =>
  api<{ ok: true }>(`/api/badminton/teams/${id}`, { method: "DELETE" });

export const getBadmintonMatches = (sportSlug: string) =>
  api<BadmintonMatch[]>(`/api/badminton/${sportSlug}/matches`);

export const createBadmintonMatch = (sportSlug: string, match: BadmintonMatchDraft) =>
  api<BadmintonMatch>(`/api/badminton/${sportSlug}/matches`, {
    method: "POST",
    body: JSON.stringify(match)
  });

export const updateBadmintonMatch = (id: number, match: BadmintonMatchDraft) =>
  api<BadmintonMatch>(`/api/badminton/matches/${id}`, {
    method: "PUT",
    body: JSON.stringify(match)
  });

export const deleteBadmintonMatch = (id: number) =>
  api<{ ok: true }>(`/api/badminton/matches/${id}`, { method: "DELETE" });
